/**
 * localizedMetadata directive
 * Collapsible section on content forms (VOD, Show, Season, Episode, Live Event)
 * for managing per-language metadata cards. Also reused (configured for
 * text-only or info-with-cover entities) for Modules, Shelves, Categories/Rows,
 * Content Sets and Partner Products.
 */

const DEFAULT_NAME_KEY = 'name'
const DEFAULT_NAME_LABEL = 'Title'

/**
 * Per-entity-type configuration. Keeps the directive + modal entity-agnostic.
 *
 * Config keys:
 *   - verticalKey / horizontalKey / coverKey: image-URL field names on the
 *     localization (any subset may be set)
 *   - verticalUploadType / horizontalUploadType / coverUploadType: backend
 *     'type' parameter for _uploadFile
 *   - uploadUrlFn: optional (entity) => string returning a non-default URL
 *   - hideDirectorsStars: skips director/star rows
 *   - noImages: convenience for "no vertical / horizontal slots" (cover may
 *     still render if coverKey is set)
 *   - hideDescription: skips description row entirely
 *   - nameKey: model property the "title" input writes to ('name'|'title')
 *   - nameLabel / namePlaceholder: default label/placeholder for the title
 *     input (each call site can override via directive attribute)
 *   - nameRequired: default required-ness for the title input (default true)
 *   - descriptionPlaceholder: placeholder for the description textarea
 *   - viewOnly: read-only flavor (eye cards, modal opens locked)
 *   - hideWhenEmpty: omit the whole section when getLocalizations() is empty
 *     (shelfAsRow / MW-13552 only — do not set on editable types)
 *   - sectionLabel: collapsible-section heading text
 *   - flushLayout: full-width columns (no col-xs-offset-1) — per type only
 *   - flatSectionHeader: static section title (no caret toggle); body always visible
 *   - contentSectionLayout: col-xs-1 external caret + col-xs-2 title header
 *     (vod/show/season/episode/liveEvent only; contentSet/partnerProduct keep
 *     the offset monolith header)
 *   - deleteNoun: word used in the delete-confirmation prompt
 *   - hintText: text below the modal heading
 *   - imagesBeforeTitle: when true, modal renders Image Assets before Title
 *     (VOD / Show / Season / Episode / Live Event only)
 *
 * Visual chrome (card radius, list gap) is NOT a config flag. Scope it with
 * the root class `localized-metadata--{{entityType}}` so a single-module
 * ticket cannot restyle every consumer. See directives/localizedMetadata/README.md.
 *
 * @type {Object}
 */
export const LOCALIZED_METADATA_TYPE_CONFIG = {
  vod: {
    verticalKey: 'imageUrl',
    horizontalKey: 'imageBackgroundUrl',
    hideDirectorsStars: false,
    verticalUploadType: 'vod',
    horizontalUploadType: 'background_vod',
    uploadUrlFn: null,
    imagesBeforeTitle: true,
    contentSectionLayout: true,
    sectionLabel: 'Localized Metadata',
    deleteNoun: 'Metadata',
    hintText: 'You can manage metadata for the selected language.'
  },
  show: {
    verticalKey: 'portraitImageUrl',
    horizontalKey: 'backgroundImageUrl',
    hideDirectorsStars: false,
    verticalUploadType: 'PORTRAIT',
    horizontalUploadType: 'BACKGROUND',
    uploadUrlFn: (entity) => entity._url + '/upload',
    imagesBeforeTitle: true,
    contentSectionLayout: true,
    sectionLabel: 'Localized Metadata',
    deleteNoun: 'Metadata',
    hintText: 'You can manage metadata for the selected language.'
  },
  season: {
    verticalKey: 'portraitImageUrl',
    horizontalKey: 'backgroundImageUrl',
    hideDirectorsStars: false,
    verticalUploadType: 'PORTRAIT',
    horizontalUploadType: 'BACKGROUND',
    uploadUrlFn: (entity) => entity._url + '/upload',
    imagesBeforeTitle: true,
    contentSectionLayout: true,
    sectionLabel: 'Localized Metadata',
    deleteNoun: 'Metadata',
    hintText: 'You can manage metadata for the selected language.'
  },
  episode: {
    verticalKey: 'portraitImageUrl',
    horizontalKey: 'backgroundImageUrl',
    hideDirectorsStars: false,
    verticalUploadType: 'PORTRAIT',
    horizontalUploadType: 'BACKGROUND',
    uploadUrlFn: (entity) => entity._url + '/upload',
    imagesBeforeTitle: true,
    contentSectionLayout: true,
    sectionLabel: 'Localized Metadata',
    deleteNoun: 'Metadata',
    hintText: 'You can manage metadata for the selected language.'
  },
  liveEvent: {
    verticalKey: 'verticalPosterUrl',
    horizontalKey: 'videoPosterUrl',
    hideDirectorsStars: true,
    verticalUploadType: 'vertical_live_event',
    horizontalUploadType: 'video_live_event',
    uploadUrlFn: null,
    imagesBeforeTitle: true,
    contentSectionLayout: true,
    sectionLabel: 'Localized Metadata',
    deleteNoun: 'Metadata',
    hintText: 'You can manage metadata for the selected language.'
  },
  module: {
    noImages: true,
    hideDirectorsStars: true,
    // MW-13588 / Qase 38624: Module Add/Edit Language has no Description field.
    hideDescription: true,
    nameKey: 'title',
    flushLayout: true,
    flushHeaderColumnClass: 'pl-0',
    sectionBodyClass: 'pl-20',
    sectionLabel: 'Localized Module'
  },
  shelf: {
    noImages: true,
    hideDirectorsStars: true,
    nameKey: 'name',
    flushLayout: true,
    sectionLabel: 'Localized Shelf'
  },
  multiSet: {
    noImages: true,
    hideDirectorsStars: true,
    nameKey: 'name',
    flushLayout: true,
    sectionLabel: 'Localized Shelf'
  },
  categoryRow: {
    noImages: true,
    hideDirectorsStars: true,
    nameKey: 'title',
    flushLayout: true,
    flatSectionHeader: true,
    sectionLabel: 'Localized Info'
  },
  shelfAsRow: {
    noImages: true,
    hideDirectorsStars: true,
    nameKey: 'title',
    viewOnly: true,
    flushLayout: true,
    flatSectionHeader: true,
    hideWhenEmpty: true,
    sectionLabel: 'Localized Info'
  },
  partnerProduct: {
    verticalKey: 'verticalImageUrl',
    horizontalKey: 'coverImageUrl', // NB: horizontal slot is stored in `coverImageUrl` on PartnerProduct
    verticalUploadType: 'partner_app_vertical',
    horizontalUploadType: 'partner_app_horizontal',
    uploadUrlFn: null,
    hideDirectorsStars: true,
    flushLayout: true,
    nameKey: 'name',
    nameLabel: 'Product Name',
    namePlaceholder: 'Type Name',
    descriptionPlaceholder: 'Type Description',
    sectionLabel: 'Localized Info',
    deleteNoun: 'Info',
    // MW-13013: Partner Product Add/Edit Language uses a narrower centered form.
    // Extra windowClass stays off VOD/Live/Shows and Content Sets.
    modalHostClass: 'localized-metadata-modal--partner-product'
  },
  contentSet: {
    coverKey: 'coverImageUrl',
    uploadUrlFn: null,
    hideDirectorsStars: true,
    flushLayout: true,
    nameKey: 'name',
    nameLabel: 'Name',
    namePlaceholder: 'Name',
    descriptionPlaceholder: 'Description Client App',
    sectionLabel: 'Localized Info',
    deleteNoun: 'Info'
  }
}

/**
 * localizedMetadata directive
 *
 * @class localizedMetadata
 * @memberof mwApp/common
 *
 * @example
 * <localized-metadata entity="vm.vod"
 *                     entity-type="vod"
 *                     form="form"></localized-metadata>
 *
 * @example
 * <localized-metadata entity="vm.cs"
 *                     entity-type="contentSet"
 *                     cover-upload-type="cover_vod_set"
 *                     name-placeholder="VOD Set name"
 *                     form="form"></localized-metadata>
 */
/* @ngInject */
export function localizedMetadata ($uibModal, Localization) {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/localizedMetadata/localizedMetadata.html',
    scope: {
      entity: '=',
      entityType: '@',
      form: '=',
      localizationsSource: '@?',
      nameLabel: '@?',
      namePlaceholder: '@?',
      nameRequired: '@?',
      coverUploadType: '@?'
    },
    link: function (scope) {
      const cfg = LOCALIZED_METADATA_TYPE_CONFIG[scope.entityType] || {}
      scope.config = cfg

      // Resolve language tags to human-readable names for the cards. Source is
      // the global ISO-639-1 code -> name map (memoized; one HTTP call shared
      // across all directive instances). Falls back to the raw tag while the
      // list loads or if a tag is unknown.
      scope.languageNames = {}
      scope.languageNameFor = (tag) => (tag && scope.languageNames[tag]) || tag || ''
      Localization.getLanguageNames()
        .then((codeToName) => { scope.languageNames = codeToName || {} })
        .catch(() => { scope.languageNames = {} })

      const nameKey = cfg.nameKey || DEFAULT_NAME_KEY
      const sourceProp = scope.localizationsSource || 'localizations'
      const isViewOnly = !!cfg.viewOnly

      // Resolve effective label/placeholder/required from attribute overrides
      // first, then config defaults, then sensible fallback.
      const effectiveNameLabel = scope.nameLabel || cfg.nameLabel || DEFAULT_NAME_LABEL
      const effectiveNamePlaceholder = scope.namePlaceholder || cfg.namePlaceholder || effectiveNameLabel
      const effectiveDescriptionPlaceholder = cfg.descriptionPlaceholder || 'Description'
      const attrNameRequired = (scope.nameRequired || '').toLowerCase()
      const effectiveNameRequired = attrNameRequired === 'false'
        ? false
        : (cfg.nameRequired !== false)
      const effectiveCoverUploadType = scope.coverUploadType || cfg.coverUploadType || null

      // For editable entities ensure the localizations array exists so we can
      // bind cards to it directly. For view-only sources (shelfAsRow reading
      // from a foreign property) we don't auto-initialize — the parent
      // controller owns that field.
      if (!isViewOnly && !Array.isArray(scope.entity.localizations)) {
        scope.entity.localizations = []
      }

      // View-only and flatSectionHeader sections render without caret toggle.
      // Other editable sections start collapsed and toggle on click.
      scope.expanded = isViewOnly || !!cfg.flatSectionHeader

      scope.getLocalizations = function () {
        const list = scope.entity ? scope.entity[sourceProp] : null
        return Array.isArray(list) ? list : []
      }

      scope.toggle = function () {
        if (isViewOnly || cfg.flatSectionHeader) return
        scope.expanded = !scope.expanded
      }

      function markFormDirty () {
        if (scope.form && typeof scope.form.$setDirty === 'function') {
          scope.form.$setDirty()
        }
      }

      function emptyLocalization () {
        const tpl = { language: '' }
        tpl[nameKey] = ''
        if (!cfg.hideDescription) {
          tpl.description = ''
        }
        if (cfg.verticalKey) tpl[cfg.verticalKey] = ''
        if (cfg.horizontalKey) tpl[cfg.horizontalKey] = ''
        if (cfg.coverKey) tpl[cfg.coverKey] = ''
        if (!cfg.hideDirectorsStars) {
          tpl.directors = ''
          tpl.stars = ''
        }
        return tpl
      }

      function buildImageDefaults () {
        const out = {}
        if (cfg.verticalKey) out.vertical = scope.entity[cfg.verticalKey]
        if (cfg.horizontalKey) out.horizontal = scope.entity[cfg.horizontalKey]
        if (cfg.coverKey) out.cover = scope.entity[cfg.coverKey]
        return out
      }

      function openModal (existing, viewMode) {
        const isEdit = !!existing && !viewMode
        return $uibModal.open({
          animation: false,
          size: 'lg',
          // The app forces .modal-content transparent globally; this windowClass
          // restores an opaque surface so the modal doesn't show the page behind it.
          // Content Set / Partner Product add host modifiers; VOD keeps the base class.
          windowClass: [
            'localized-metadata-modal',
            cfg.coverKey ? 'localized-metadata-modal--content-set' : null,
            cfg.modalHostClass || null
          ].filter(Boolean).join(' '),
          templateUrl: 'components/common/directives/localizedMetadata/localizedMetadata.modal.html',
          controller: 'LocalizedMetadataModalCtrl as vm',
          resolve: {
            entity: () => scope.entity,
            config: () => cfg,
            isEdit: () => isEdit,
            viewOnly: () => !!viewMode,
            nameLabel: () => effectiveNameLabel,
            namePlaceholder: () => effectiveNamePlaceholder,
            nameRequired: () => effectiveNameRequired,
            descriptionPlaceholder: () => effectiveDescriptionPlaceholder,
            coverUploadType: () => effectiveCoverUploadType,
            localization: () => existing ? angular.copy(existing) : emptyLocalization(),
            excludedLanguages: () => scope.getLocalizations()
              .map(l => l.language)
              .filter(tag => !existing || tag !== existing.language),
            defaults: () => ({
              description: scope.entity.description,
              directors: scope.entity.directors,
              stars: scope.entity.stars,
              images: buildImageDefaults()
            })
          }
        }).result.then(
          (updated) => {
            if (viewMode) return
            const list = scope.getLocalizations()
            if (isEdit) {
              const idx = list.indexOf(existing)
              if (idx >= 0) {
                list.splice(idx, 1, updated)
              }
            } else {
              list.push(updated)
            }
            markFormDirty()
          },
          () => {} // dismissed
        )
      }

      scope.addLanguage = function () {
        if (isViewOnly) return
        openModal(null, false)
      }

      scope.editLanguage = function (loc) {
        if (isViewOnly) return
        openModal(loc, false)
      }

      scope.viewLanguage = function (loc) {
        openModal(loc, true)
      }

      scope.deleteLanguage = function (loc) {
        if (isViewOnly) return
        const list = scope.getLocalizations()
        const idx = list.indexOf(loc)
        if (idx >= 0) {
          list.splice(idx, 1)
          markFormDirty()
        }
      }
    }
  }
}
